/**
 * Affichage financier Admin — sépare total commande / ledger / reste à encaisser.
 * Ne pas dériver « remboursé » comme (total − net) : faux quand total > net (complément dû).
 * Ne pas dériver « complément dû » comme (total − netEncaisse) après refund produit :
 * paidGross couvre le total ⇒ amountDue = 0 même si net < total.
 */

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

export function sumLedgerEncaisse(paiements = []) {
  return roundMoney(
    (paiements || [])
      .filter((p) => p.statutPaiement === 'VALIDE' && Number(p.montant) > 0)
      .reduce((s, p) => s + Number(p.montant), 0)
  );
}

export function sumLedgerRembourse(paiements = []) {
  return roundMoney(
    Math.abs(
      (paiements || [])
        .filter((p) => p.statutPaiement === 'REMBOURSE' && Number(p.montant) < 0)
        .reduce((s, p) => s + Number(p.montant), 0)
    )
  );
}

export function sumPendingPoidsComplement(paiements = []) {
  return roundMoney(
    (paiements || [])
      .filter((p) => {
        if (p.statutPaiement !== 'EN_ATTENTE' || !(Number(p.montant) > 0)) return false;
        const meta = p.providerMetadata || {};
        if (meta.source === 'POIDS_COMPLEMENT') return true;
        let ref = p.reference;
        if (typeof ref === 'string') {
          try {
            ref = JSON.parse(ref);
          } catch {
            return false;
          }
        }
        return ref?.type === 'COMPLEMENT_POIDS';
      })
      .reduce((s, p) => s + Number(p.montant), 0)
  );
}

/**
 * @returns {{
 *   totalCommande: number,
 *   netEncaisse: number,
 *   montantRembourse: number,
 *   resteAEncaisser: number,
 *   complementsPending: number,
 *   isPartiellementRembourse: boolean,
 *   isRembourseTotal: boolean,
 *   hasResteAEncaisser: boolean,
 * }}
 */
export function getCommandeFinancier(cmd) {
  const paiements = cmd?.paiements || [];
  const totalCommande = roundMoney(Number(cmd?.total ?? cmd?.prixFinal ?? 0));
  const encaisse = sumLedgerEncaisse(paiements);
  const montantRembourse = sumLedgerRembourse(paiements);
  const complementsPending = sumPendingPoidsComplement(paiements);

  let netEncaisse =
    cmd?.totalNet != null && Number.isFinite(Number(cmd.totalNet))
      ? roundMoney(Number(cmd.totalNet))
      : null;
  if (netEncaisse == null || paiements.length > 0) {
    // Ledger prioritaire dès qu'il y a des mouvements.
    if (encaisse > 0.009 || montantRembourse > 0.009) {
      netEncaisse = roundMoney(encaisse - montantRembourse);
    } else if (netEncaisse == null) {
      netEncaisse = totalCommande;
    }
  }

  // unpaidGap = total − paidGross (ignore refunds). Un refund ne crée jamais de complément.
  const unpaidGap = roundMoney(Math.max(0, totalCommande - encaisse));
  // Pending poids = source explicite du complément ; sinon gap de sous-paiement.
  const resteAEncaisser = roundMoney(
    complementsPending > 0.009 ? complementsPending : unpaidGap
  );
  const isRembourseTotal = cmd?.statutPaiement === 'REMBOURSE';
  // Remboursement réel uniquement (mouvements REMBOURSE). Jamais (total − net).
  const isPartiellementRembourse =
    !isRembourseTotal &&
    cmd?.statutPaiement === 'VALIDE' &&
    montantRembourse > 0.01 &&
    netEncaisse > 0.01;
  const hasResteAEncaisser = resteAEncaisser > 0.01;

  return {
    totalBrut: totalCommande,
    totalCommande,
    totalNet: netEncaisse,
    netEncaisse,
    paidGross: encaisse,
    montantRembourse,
    resteAEncaisser,
    complementsPending,
    isPartiellementRembourse,
    isRembourseTotal,
    hasResteAEncaisser,
    /** Statut UI dérivé du ledger — priorité complément si vrai reste dû. */
    financeStatus: isRembourseTotal
      ? 'REMBOURSE_TOTAL'
      : hasResteAEncaisser
        ? 'COMPLEMENT_A_ENCAISSER'
        : isPartiellementRembourse
          ? 'PARTIELLEMENT_REMBOURSE'
          : cmd?.statutPaiement === 'VALIDE'
            ? 'PAYE'
            : String(cmd?.statutPaiement || 'INCONNU'),
  };
}

/**
 * Montant principal listes / cards — projection UX uniquement.
 * Pas de refund → total commande (historique).
 * Refund partiel → net encaissé.
 * Refund total → 0.
 * Complément poids sans refund → reste sur total commande (jamais « remboursé »).
 */
export function getListPrincipalAmount(cmd) {
  const fin = getCommandeFinancier(cmd);
  if (fin.isRembourseTotal) return 0;
  if (fin.montantRembourse > 0.009 && fin.netEncaisse <= 0.009) return 0;
  if (fin.montantRembourse > 0.009) return fin.netEncaisse;
  return fin.totalCommande;
}

export function getPendingComplementCheckoutUrl(paiements = []) {
  for (const p of paiements || []) {
    if (p.statutPaiement !== 'EN_ATTENTE' || !(Number(p.montant) > 0)) continue;
    const meta = p.providerMetadata || {};
    let ref = p.reference;
    if (typeof ref === 'string') {
      try {
        ref = JSON.parse(ref);
      } catch {
        ref = null;
      }
    }
    if (meta.source === 'POIDS_COMPLEMENT' || ref?.type === 'COMPLEMENT_POIDS') {
      return ref?.url || null;
    }
  }
  return null;
}
